/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.18.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AccessComponentKeyEnum } from '../../../modules/role/constants/guard/guardConstants';
import {
    makeSelectGuardComponentEnabled,
    makeSelectGuardComponents,
} from '../../../modules/role/selectors/guardSelector';
import {
    dropTableFilterAndSort,
    loadTableListAction,
    loadTableMeta as loadTableMetaAction,
    sendTableFields as sendTableFieldsAction,
    setTableCurrentName,
    setTableFilter as setTableFilterAction,
    setTableFilterPresetAction,
    setTableSortAction,
    switchCheckbox as switchCheckboxAction,
    tableButtonsSetModeAction,
} from '../actions/tableActions';
import { makeSelectorInitBasketList } from '../components/footerModal/Basket/basketSelectors';
import {
    makeSelectorTableBasketAllIn,
    makeSelectorTableBasketCount,
    makeSelectorTableCount,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableFilter,
    makeSelectorTableIsLoading,
    makeSelectorTableRemovedRowIndexes,
    makeSelectorTableSort,
} from '../selectors/tableSelectors';
import {
    IDropTableFilterAndSortPayload,
    ILoadTableListPayload,
    ILoadTableMetaPayload,
    ISendTableFieldsPayload,
    ISetTableFilterPayload,
    ISetTableFilterPresetPayload,
    ISetTableSortPayload,
    ISwitchCheckboxPayload,
    TableNameType,
    TTableButtonsSetModePayload,
} from '../types/tableTypes';
import { useTableResize } from './useTableResize';

export const useTable = (tableName: TableNameType) => {
    const dispatch = useDispatch();

    const loadTableMeta = useCallback((payload?: ILoadTableMetaPayload) => {
        dispatch(loadTableMetaAction(payload || { tableName }));
    }, [tableName, dispatch]);

    const loadTableList = useCallback((payload?: ILoadTableListPayload) => {
        dispatch(loadTableListAction(payload));
    }, [dispatch]);

    const setTableSort = useCallback((payload: ISetTableSortPayload) => {
        dispatch(setTableSortAction(payload));
    }, [dispatch]);

    const setTableFilter = useCallback((payload: ISetTableFilterPayload) => {
        dispatch(setTableFilterAction(payload));
    }, [dispatch]);

    const dropFilterAndSort = useCallback((payload: IDropTableFilterAndSortPayload) => {
        dispatch(dropTableFilterAndSort(payload));
    }, [dispatch]);

    const switchCheckbox = useCallback((payload: ISwitchCheckboxPayload) => {
        dispatch(switchCheckboxAction(payload));
    }, [dispatch]);

    const setCurrentTableName = useCallback((payload: TableNameType) => {
        dispatch(setTableCurrentName(payload));
    }, [dispatch]);

    const sendTableFields = useCallback((payload: ISendTableFieldsPayload) => {
        dispatch(sendTableFieldsAction(payload));
    }, [dispatch]);

    const setTableFilterPreset = useCallback((payload: ISetTableFilterPresetPayload) => {
        dispatch(setTableFilterPresetAction(payload));
    }, [dispatch]);

    const setTableMode = useCallback((payload: Omit<TTableButtonsSetModePayload, 'tableName'>) => {
        dispatch(tableButtonsSetModeAction({ ...payload, tableName }));
    }, [dispatch, tableName]);

    const selectedFilters = useSelector(makeSelectorTableFilter(tableName));
    const isFilterSelected = useCallback(() => {
        return Object.keys(selectedFilters).length !== 0;
    }, [selectedFilters]);

    return {
        basketAllIn: useSelector(makeSelectorTableBasketAllIn(tableName)),
        basketCount: useSelector(makeSelectorTableBasketCount(tableName)),
        componentsFeature: useSelector(makeSelectGuardComponents()),
        count: useSelector(makeSelectorTableCount(tableName)),
        data: useSelector(makeSelectorTableData(tableName)),
        fields: useSelector(makeSelectorTableFields(tableName)),
        isLoading: useSelector(makeSelectorTableIsLoading(tableName)),
        isTableResizeEnabled: useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.columnResize)),
        removedRowIndexes: useSelector(makeSelectorTableRemovedRowIndexes()),
        selectedFilters: useSelector(makeSelectorTableFilter(tableName)),
        sort: useSelector(makeSelectorTableSort(tableName)),
        tableCheckedItems: useSelector(makeSelectorInitBasketList()),

        dropFilterAndSort,
        isFilterSelected,
        loadTableList,
        loadTableMeta,
        saveResizedChange: useTableResize(tableName),
        sendTableFields,
        setTableMode,
        setCurrentTableName,
        setTableFilterPreset,
        setTableSort,
        switchCheckbox,
        setTableFilter,
    };
};
